import { FirebaseApp, getApps, initializeApp } from 'firebase/app';
import { getMessaging, getToken, Messaging } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY || 'AIzaSyBhYL7O5mJbYRpchmYstZuNNm9EkoXsISs',
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN || 'curiousjr-app.firebaseapp.com',
  databaseURL:
    process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL || 'https://curiousjr-app.firebaseio.com',
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID || 'curiousjr-app',
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET || 'curiousjr-app.appspot.com',
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID || '979752599489',
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID || '1:979752599489:web:88435d173d4a8e81d6f940',
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID || 'G-YLS8T7FSRC',
};
// Initialize Firebase app (only if not already initialized)
let app: FirebaseApp;
if (getApps().length === 0) {
  app = initializeApp(firebaseConfig);
} else {
  app = getApps()[0];
}

// Get messaging instance (only in browser)
// Note: Messaging will be initialized lazily after service worker is registered
let messaging: Messaging | null = null;

/**
 * Initialize messaging instance (only after service worker is ready)
 */
const initializeMessaging = (): Messaging | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!messaging) {
    try {
      if ('serviceWorker' in navigator) {
        console.log('Initializing Firebase Messaging...');
        messaging = getMessaging(app);
        console.log('Firebase Messaging initialized successfully');
      } else {
        console.warn('Service Worker is not supported. Push notifications may not work.');
      }
    } catch (error) {
      console.error('Failed to initialize Firebase Messaging:', error);
    }
  }

  return messaging;
};

/**
 * Register service worker for FCM (all users; no role restriction)
 * @returns Promise<ServiceWorkerRegistration | null>
 */
const registerServiceWorker = async (): Promise<ServiceWorkerRegistration | null> => {
  if (typeof window === 'undefined') {
    console.warn('Service Worker: Not in browser environment');
    return null;
  }

  if (!('serviceWorker' in navigator)) {
    console.warn('Service Worker: Not supported in this browser');
    return null;
  }

  try {
    // Check if service worker is already registered
    let registration = await navigator.serviceWorker.getRegistration();

    if (registration) {
      console.log('Service Worker: Already registered, waiting for ready...');
      // Wait for service worker to be ready
      if (registration.installing) {
        console.log('Service Worker: Installing...');
        await new Promise<void>(resolve => {
          const installingWorker = registration!.installing!;
          installingWorker.addEventListener('statechange', () => {
            console.log(`Service Worker: State changed to ${installingWorker.state}`);
            if (installingWorker.state === 'activated') {
              resolve();
            }
          });
        });
      }
      await navigator.serviceWorker.ready;
      console.log('Service Worker: Ready');
      return registration;
    }

    // Register the service worker
    console.log('Service Worker: Registering...');
    registration = await navigator.serviceWorker.register('/v2/firebase-messaging-sw.js', {
      scope: '/',
    });

    console.log('Service Worker: Registration started');

    // Wait for service worker to be ready
    if (registration.installing) {
      console.log('Service Worker: Installing new worker...');
      await new Promise<void>(resolve => {
        const installingWorker = registration.installing!;
        installingWorker.addEventListener('statechange', () => {
          console.log(`Service Worker: State changed to ${installingWorker.state}`);
          if (installingWorker.state === 'activated') {
            resolve();
          }
        });
      });
    } else if (registration.waiting) {
      console.log('Service Worker: Waiting, activating...');
      // If waiting, activate it
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      await navigator.serviceWorker.ready;
    } else {
      console.log('Service Worker: Already active, waiting for ready...');
      // Already active, wait for ready
      await navigator.serviceWorker.ready;
    }

    console.log('Service Worker: Registered and ready');
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
};

/**
 * Get FCM token
 * @returns Promise<string | null> - FCM token or null if unavailable
 */
export const getFCMToken = async (): Promise<string | null> => {
  if (typeof window === 'undefined') return null;

  try {
    const registration = await registerServiceWorker();
    if (!registration) return null;

    // ⬅️ FIX: wait until SW is active
    await navigator.serviceWorker.ready;

    const messagingInstance = initializeMessaging();
    if (!messagingInstance) return null;

    const vapidKey = process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY || '';
    const token = await getToken(messagingInstance, {
      vapidKey,
      serviceWorkerRegistration: registration,
    });

    return token ?? null;
  } catch (error) {
    console.error('FCM token error:', error);
    return null;
  }
};

/**
 * Request notification permission
 * @returns Promise<NotificationPermission> - Permission status
 */
export const requestNotificationPermission = async (): Promise<NotificationPermission> => {
  if (typeof window === 'undefined' || !('Notification' in window)) {
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  if (Notification.permission === 'denied') {
    return 'denied';
  }

  // Request permission
  const permission = await Notification.requestPermission();
  return permission;
};

/**
 * Set up message handler for foreground notifications.
 * Listens for postMessage events from the service worker which forwards
 * push data when the app is in the foreground (visible tab).
 */
export const onMessageListener = (
  callback: (payload: unknown) => void
): (() => void) | null => {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return null;
  }

  const handler = (event: MessageEvent) => {
    if (event.data?.type === 'PUSH_NOTIFICATION') {
      console.log('[onMessageListener] Received forwarded push from SW');
      callback(event.data.payload);
    }
  };

  navigator.serviceWorker.addEventListener('message', handler);
  console.log('[onMessageListener] Foreground listener attached via SW message');

  return () => {
    navigator.serviceWorker.removeEventListener('message', handler);
  };
};

/**
 * Unregister all service workers
 * Should be called on logout to clean up
 * @returns Promise<boolean> - Success status
 */
export const unregisterServiceWorker = async (): Promise<boolean> => {
  if (typeof window === 'undefined') {
    console.warn('Service Worker: Not in browser environment');
    return false;
  }

  if (!('serviceWorker' in navigator)) {
    console.warn('Service Worker: Not supported in this browser');
    return false;
  }

  try {
    // Get all service worker registrations
    const registrations = await navigator.serviceWorker.getRegistrations();

    if (!registrations || registrations.length === 0) {
      console.log('Service Worker: No registrations found to unregister');
      // Reset the messaging instance so it can be re-initialized later
      messaging = null;
      return true;
    }

    console.log(`Service Worker: Found ${registrations.length} registration(s) to unregister`);

    // Unregister all service workers
    const unregisterPromises = registrations.map(registration => registration.unregister());

    const results = await Promise.all(unregisterPromises);
    const allUnregistered = results.every(result => result === true);

    if (allUnregistered) {
      console.log(`Service Worker: Successfully unregistered ${registrations.length} worker(s)`);
      // Reset the messaging instance so it can be re-initialized later
      messaging = null;
      return true;
    } else {
      const successCount = results.filter(result => result === true).length;
      console.warn(
        `Service Worker: Partially unregistered (${successCount}/${registrations.length} workers)`
      );
      // Reset the messaging instance even if some failed
      messaging = null;
      return false;
    }
  } catch (error) {
    console.error('Service Worker: Error during unregistration:', error);
    // Reset the messaging instance even on error
    messaging = null;
    return false;
  }
};

export { app, messaging };
export default app;
